import { ReactElement, useState } from 'react'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import {
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import type { UserTokenSpendDTO } from '@api/fastAPI.schemas'
import { formatDateTime } from '../../../utils'
import { formatCost, formatTokens } from '../utils'

interface Props {
  user: UserTokenSpendDTO
  // Cost of the most expensive user, to scale the bar.
  maxCostUsd: number
}

const COLUMN_COUNT = 7

const UserTokenSpendRow = ({ user, maxCostUsd }: Props): ReactElement => {
  const [open, setOpen] = useState(false)
  const costShare = maxCostUsd > 0 ? (user.cost_usd / maxCostUsd) * 100 : 0

  return (
    <>
      <TableRow data-cy="token-spend-row" sx={{ '& > td': { borderBottom: 'unset' } }}>
        <TableCell padding="checkbox">
          <Tooltip title={open ? 'Modelle ausblenden' : 'Modelle anzeigen'}>
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{user.name || user.user_id}</Typography>
          {user.email && (
            <Typography variant="caption" color="text.secondary">
              {user.email}
            </Typography>
          )}
        </TableCell>
        <TableCell>{user.organisation_names.join(', ') || '—'}</TableCell>
        <TableCell align="right">{formatTokens(user.runs)}</TableCell>
        <TableCell align="right">
          {formatTokens(user.input_tokens)} / {formatTokens(user.output_tokens)}
        </TableCell>
        <TableCell align="right" sx={{ minWidth: 160 }}>
          {user.runs_without_cost > 0 ? (
            <Tooltip
              title={`${user.runs_without_cost} Turn(s) ohne bekannte Kosten – Wert ist eine Untergrenze`}
            >
              <span>≥ {formatCost(user.cost_usd)}</span>
            </Tooltip>
          ) : (
            formatCost(user.cost_usd)
          )}
          <LinearProgress
            variant="determinate"
            value={costShare}
            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
          />
        </TableCell>
        <TableCell>{formatDateTime(user.last_run_at)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={COLUMN_COUNT}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1, ml: 6 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Modell</TableCell>
                    <TableCell align="right">Turns</TableCell>
                    <TableCell align="right">Input / Output Tokens</TableCell>
                    <TableCell align="right">Kosten</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {user.models.map(model => (
                    <TableRow key={model.model ?? 'unknown'}>
                      <TableCell>{model.model || 'unbekannt'}</TableCell>
                      <TableCell align="right">{formatTokens(model.runs)}</TableCell>
                      <TableCell align="right">
                        {formatTokens(model.input_tokens)} / {formatTokens(model.output_tokens)}
                      </TableCell>
                      <TableCell align="right">{formatCost(model.cost_usd)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export default UserTokenSpendRow
